import {
  SummaryLine,
  formatSummaryLine,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import {
  MINI_COG_ANSWER_KEYS,
  MINI_COG_DEFAULT_VALUE,
  MINI_COG_INTERPRETATION,
  MINI_COG_MAX,
  MINI_COG_POSITIVE_CUT_POINT,
  MINI_COG_SENSITIVE_CUT_POINT,
  MINI_COG_SUMMARY_LABELS,
} from "./initial-f2f-mini-cog-constant";
import { Answers, toSection } from "./initial-f2f-note-answers";
import { entriesToLines } from "./initial-f2f-note-lines";
import { MiniCogValue, NoteSection } from "./initial-f2f-types";

const toScore = (value: string): number | null =>
  value === "" ? null : Number(value);

/** Word recall plus clock draw, or null until both are entered. */
export const computeMiniCogTotal = (value: MiniCogValue): number | null => {
  const recall = toScore(value.wordRecallScore);
  const clock = toScore(value.clockDrawScore);
  return recall === null || clock === null ? null : recall + clock;
};

export const interpretMiniCogTotal = (total: number): string => {
  if (total < MINI_COG_POSITIVE_CUT_POINT)
    return MINI_COG_INTERPRETATION.positive;
  if (total < MINI_COG_SENSITIVE_CUT_POINT)
    return MINI_COG_INTERPRETATION.borderline;
  return MINI_COG_INTERPRETATION.negative;
};

export const buildMiniCogSummaryEntries = (
  value: MiniCogValue
): SummaryLine[] => {
  const labels = MINI_COG_SUMMARY_LABELS;
  const lines: SummaryLine[] = [];
  if (value.registrationWords.length)
    lines.push({
      title: labels.registration,
      text: `${value.registrationWords.join(", ")}.`,
    });
  if (value.clockImage)
    lines.push({
      title: labels.clockImage,
      text: `${labels.clockImageAttached}.`,
    });
  if (value.wordListVersion)
    lines.push({
      title: labels.wordListVersion,
      text: `${value.wordListVersion}.`,
    });
  if (value.personsAnswers.trim())
    lines.push({
      title: labels.personsAnswers,
      text: `${value.personsAnswers.trim()}.`,
    });
  if (value.wordRecallScore !== "")
    lines.push({
      title: labels.wordRecall,
      text: `${value.wordRecallScore}/${MINI_COG_MAX.wordRecall}.`,
    });
  if (value.clockDrawScore !== "")
    lines.push({
      title: labels.clockDraw,
      text: `${value.clockDrawScore}/${MINI_COG_MAX.clockDraw}.`,
    });
  const total = computeMiniCogTotal(value);
  if (total !== null)
    lines.push({
      title: labels.total,
      text: `${total}/${MINI_COG_MAX.total}, ${interpretMiniCogTotal(total)}.`,
    });
  return lines;
};

export const buildMiniCogNoteSection = (
  value: MiniCogValue
): NoteSection | undefined => {
  const keys = MINI_COG_ANSWER_KEYS;
  const answers: Answers = {};
  if (value.registrationWords.length)
    answers[keys.registrationWords] = {
      value: value.registrationWords,
      selected: value.registrationWords,
    };
  if (value.clockImage) answers[keys.clockImage] = { value: value.clockImage };
  if (value.wordListVersion)
    answers[keys.wordListVersion] = { value: value.wordListVersion };
  if (value.personsAnswers.trim())
    answers[keys.personsAnswers] = { value: value.personsAnswers.trim() };
  if (value.wordRecallScore !== "")
    answers[keys.wordRecallScore] = { value: value.wordRecallScore };
  if (value.clockDrawScore !== "")
    answers[keys.clockDrawScore] = { value: value.clockDrawScore };
  const total = computeMiniCogTotal(value);
  if (total !== null) answers[keys.totalScore] = { value: String(total) };
  const entries = buildMiniCogSummaryEntries(value);
  return toSection(
    answers,
    entries.map(formatSummaryLine),
    entriesToLines(entries)
  );
};

const textOf = (answers: Answers, key: string): string => {
  const value = answers[key]?.value;
  return typeof value === "string" ? value : "";
};

export const parseMiniCogAnswers = (answers: Answers): MiniCogValue => {
  const keys = MINI_COG_ANSWER_KEYS;
  const words = answers[keys.registrationWords];
  return {
    ...MINI_COG_DEFAULT_VALUE,
    registrationWords:
      words?.selected ?? (Array.isArray(words?.value) ? words.value : []),
    clockImage: textOf(answers, keys.clockImage),
    wordListVersion: textOf(answers, keys.wordListVersion),
    personsAnswers: textOf(answers, keys.personsAnswers),
    wordRecallScore: textOf(answers, keys.wordRecallScore),
    clockDrawScore: textOf(answers, keys.clockDrawScore),
  };
};
